import { useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../context/AuthContext';
import type { AgapellaAccessState } from '../types/agapellaSubscription';
import { api } from './api';

/**
 * CORE-1538 — состояние подписочного доступа одно на приложение.
 *
 * Спрашивают его сразу несколько поверхностей (окно пробного доступа, бейджи
 * на карточках), и каждая со своим запросом — это десяток одинаковых GET на
 * витрине мастера. Здесь один запрос на аккаунт и общий ответ; после старта
 * trial или возврата в приложение его перечитывают явно.
 */
let current: AgapellaAccessState | null = null;
let loadedFor: string | null = null;
const listeners = new Set<() => void>();

function emit(next: AgapellaAccessState | null): void {
  current = next;
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};
const snapshot = () => current;

/** Положить уже известное состояние (ответ старта trial) без нового запроса. */
export function setAgapellaAccess(next: AgapellaAccessState | null): void {
  emit(next);
}

export async function refreshAgapellaAccess(): Promise<AgapellaAccessState | null> {
  const owner = loadedFor;
  const next = await api.getAgapellaSubscription().catch(() => null);
  // Пока ждали, человек вышел или сменил аккаунт — чужой ответ не кладём.
  if (owner !== loadedFor) return current;
  emit(next);
  return next;
}

/** `null` — гость, ещё не загружено или сервер не ответил. */
export function useAgapellaAccess(): AgapellaAccessState | null {
  const { isAuthenticated, user } = useAuth();
  const userId = isAuthenticated ? user?.id ?? null : null;

  useEffect(() => {
    if (userId === loadedFor) return;
    loadedFor = userId;
    emit(null);
    if (userId) void refreshAgapellaAccess();
  }, [userId]);

  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Только для тестов. */
export function __resetAgapellaAccessForTests(): void {
  current = null;
  loadedFor = null;
  listeners.clear();
}
